import type { ArenaGameplayDefinition, EntityState } from './types';
import type { SpatialIndex } from './spatial-index';

const edgeDistance = (source: EntityState, target: EntityState): number => Math.hypot(source.position.x - target.position.x, source.position.y - target.position.y) - source.radius - target.radius;

const isWithinSight = (source: EntityState, target: EntityState, tolerance = 1): boolean => {
  const weapon = source.weapon;
  if (!weapon || edgeDistance(source, target) > weapon.sight * tolerance) return false;
  const combinedRadius = source.radius + target.radius;
  const sideEdgeDistance = Math.max(0, Math.abs(target.position.x - source.position.x) - combinedRadius);
  if (sideEdgeDistance > (weapon.sight - (weapon.sightClipSide ?? 0)) * tolerance) return false;
  const forwardDistance = source.owner === 'player'
    ? source.position.y - target.position.y
    : target.position.y - source.position.y;
  const rearEdgeDistance = Math.max(0, -forwardDistance - combinedRadius);
  return rearEdgeDistance <= (weapon.sight - (weapon.sightClip ?? 0)) * tolerance;
};

export function isValidTarget(source: EntityState, target: EntityState): boolean {
  if (source.owner === target.owner || target.dead || target.removed || target.deployRemaining > 0) return false;
  if (source.targetMask === 'buildings' && target.kind !== 'building' && target.kind !== 'tower') return false;
  if (source.targetMask === 'ground' && target.movement === 'air') return false;
  return true;
}

function defaultCrownTarget(entity: EntityState, entities: readonly EntityState[], _arena: ArenaGameplayDefinition): EntityState | null {
  const enemyKingSocket = entity.owner === 'player' ? 'botKing' : 'playerKing';
  const enemyTowers = entities.filter((candidate) => candidate.owner !== entity.owner && candidate.kind === 'tower');
  const king = enemyTowers.find((candidate) => candidate.arenaSocketId === enemyKingSocket) ?? null;
  const princessTowers = enemyTowers.filter((candidate) => candidate.arenaSocketId !== enemyKingSocket);
  const laneTower = princessTowers.slice().sort((left, right) => (
    Math.abs(entity.position.x - left.position.x) - Math.abs(entity.position.x - right.position.x)
    || left.id - right.id
  ))[0] ?? null;
  if (laneTower && isValidTarget(entity, laneTower)) return laneTower;
  if (king && isValidTarget(entity, king)) return king;
  return enemyTowers
    .filter((candidate) => isValidTarget(entity, candidate))
    .sort((left, right) => edgeDistance(entity, left) - edgeDistance(entity, right) || left.id - right.id)[0] ?? null;
}

export function acquireTarget(entity: EntityState, entities: readonly EntityState[], arena: ArenaGameplayDefinition, index?: SpatialIndex<EntityState>): EntityState | null {
  const sight = entity.weapon?.sight ?? 0;
  const lockedTarget = entity.targetId === null ? null : entities.find((candidate) => candidate.id === entity.targetId && !candidate.removed) ?? null;
  if (lockedTarget && isValidTarget(entity, lockedTarget) && isWithinSight(entity, lockedTarget, 1.1)) return lockedTarget;
  let nearestVisible: EntityState | null = null;
  let nearestVisibleDistance = Number.POSITIVE_INFINITY;
  const visibleCandidates = index ? index.query(entity.position, sight + entity.radius + index.maxRadius) : entities;
  for (const candidate of visibleCandidates) {
    if (!isValidTarget(entity, candidate)) continue;
    const candidateDistance = edgeDistance(entity, candidate);
    if (!isWithinSight(entity, candidate)) continue;
    if (candidateDistance < nearestVisibleDistance || (candidateDistance === nearestVisibleDistance && candidate.id < (nearestVisible?.id ?? Number.POSITIVE_INFINITY))) {
      nearestVisible = candidate;
      nearestVisibleDistance = candidateDistance;
    }
  }
  if (nearestVisible || entity.movement === 'static') return nearestVisible;
  if (lockedTarget && isValidTarget(entity, lockedTarget) && (lockedTarget.kind === 'building' || lockedTarget.kind === 'tower')) return lockedTarget;
  return defaultCrownTarget(entity, entities, arena);
}
